import React, { useState, useEffect } from "react";
import {
    Box,
    IconButton,
    Drawer,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Collapse,
    Typography,
    TextField,
    Button,
    Stack,
    Tooltip,
    Paper,
    CircularProgress,
    InputAdornment,
    FormControlLabel,
    Checkbox,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import SearchIcon from "@mui/icons-material/Search";
import { Colors } from "../../../Styles/Colors";
import { ImagePaths } from "../../../Utility/ImagePaths";
import ToastMsg from "../../../Components/ToastMsg/ToastMsg";
import RestAPIService from "../../../Utility/Services/RestAPIService";
import { Serviceurls } from "../../../Utility/API/Serviceurls";
import { AmountsCard } from "../../../Components/CommonCards/CommonCards";
import CommonDatePicker from "../../../Components/Common Components/CommonDatePicker";
import { CommonPatientDetails } from "../../../Components/Common Components/CommonComponents";
import { DateTime } from "luxon";
import { AmountFormat, formatDate } from "../../../Components/CommonFunctions/CommonFunctions";

const CREDIT_TYPE_OPTIONS = [
    { value: "Patient credit", label: "Patient" },
    { value: "Corporate credit", label: "Corporate" },
    { value: "Insurance credit", label: "Insurance" },
];

const PATIENT_HEADERS = [
    "Patient Details",
    "Patient Tag",
    "Therapy Amount",
    "Total Fractions",
    "Pervious Received Amount",
    "Received Amount",
    "Balance Amount",
];

const EVENT_HEADERS = [
    "Therapy Details",
    "Site",
    "Dose Details",
    "Technique",
    "Doctor",
    "Stimulation",
    "Credit Type",
    // "Payable Amount",
    "Previous Received Amount",
    "Refund Amount",
    "Received Amount",
    // "Balance Amount",
    "Date of payment",
    "Fractions",
    "Dose Per Fraction",
    "Completed",
    "Pending",
];

const EVENT_CELL_TIP_MAX = 20;

/** Long text in event rows: ellipsis in cell, full value in tooltip. */
const RtEventTextWithTooltip = ({ value, width = "160px" }) => {
    const text = value == null || value === "" ? "-" : String(value);
    const short = text.length <= EVENT_CELL_TIP_MAX;
    return (
        <Tooltip title={text} arrow placement="top" disableHoverListener={short}>
            <Box component="span" sx={{ display: "inline-block", width: width }}>
                {short ? text : `${text.slice(0, EVENT_CELL_TIP_MAX)}...`}
            </Box>
        </Tooltip>
    );
};

/** Short values (amounts, dates): tooltip shows same string when useful. */
const RtEventValueWithTooltip = ({ title, children, width = "160px" }) => {
    const t = title == null || title === "" ? "-" : String(title);
    const short = t.length <= 20;
    return (
        <Tooltip title={t} arrow placement="top" disableHoverListener={short}>
            <Box component="span" sx={{ display: "inline-block", width: width }}>{children}</Box>
        </Tooltip>
    );
};

const RtPatientReport = () => {
    const defaultDate = {
        fromDate: DateTime.fromJSDate(new Date()).toFormat("yyyy-MM-dd"),
        toDate: DateTime.fromJSDate(new Date()).toFormat("yyyy-MM-dd"),
        dateIndex: 1,
    };
    const [reportData, setReportData] = useState([]);
    const [summaryCards, setSummaryCards] = useState({
        total_patients: 0,
        total_payable: 0,
        total_received: 0,
        total_balance: 0,
        total_cancelled_received: 0,
    });
    const [isloader, setisloader] = useState(false);
    const [filterOpen, setFilterOpen] = useState(false);
    const [filterData, setfilterData] = useState({
        ...defaultDate,
        uhid: "",
        credit_type: [],
        technique: "",
        therapy_name: "",
    });
    const [expandedRows, setExpandedRows] = useState({});
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchBarText, setSearchBarText] = useState("");

    const TABLE_HEADER_BG = "#F5F8F7";
    const TABLE_HEADER_TEXT = "#616161";
    const [errorMsg, seterrorMsg] = useState(false);
    const [errorMsgText, seterrorMsgText] = useState("");
    const [successMsg, setsuccessMsg] = useState(false);
    const [successMsgText, setsuccessMsgText] = useState("");

    useEffect(() => {
        getReportData();
    }, []);

    const errorMessage = (msg) => {
        seterrorMsgText(msg);
        seterrorMsg(true);
    };

    const successMessage = (msg) => {
        setsuccessMsgText(msg);
        setsuccessMsg(true);
    };

    const getReportData = (filterPayload) => {
        const fd = filterPayload ?? filterData;
        try {
            setisloader(true);
            const params = new URLSearchParams({
                from_date: fd.fromDate,
                to_date: fd.toDate,
            });
            if (fd.uhid?.trim()) params.append("patient_search", fd.uhid.trim());
            if (Array.isArray(fd.credit_type) && fd.credit_type.length > 0) {
                params.append("credit_type", fd.credit_type.join(","));
            }
            if (fd.technique?.trim()) params.append("technique", fd.technique.trim());
            if (fd.therapy_name?.trim()) params.append("therapy_name", fd.therapy_name.trim());

            RestAPIService.getAll(Serviceurls.RT_PATIENT_REPORT + "?" + params.toString())
                .then((response) => {
                    if (response.data.status === "success") {
                        setReportData(response.data.data || []);
                        setSummaryCards(response.data.summary_cards || {
                            total_patients: 0,
                            total_payable: 0,
                            total_received: 0,
                            total_balance: 0,
                            total_cancelled_received: 0, 
                        });
                    }
                    setisloader(false);
                })
                .catch((e) => {
                    setisloader(false);
                    if (e?.response?.data?.message) {
                        errorMessage(e.response.data.message);
                    } else {
                        errorMessage(e.message || "Something went wrong");
                    }
                });
        } catch (e) {
            setisloader(false);
            errorMessage(e.message || "Something went wrong");
        }
    };

    const buildExportParams = (exportType) => {
        const params = new URLSearchParams({
            from_date: filterData.fromDate,
            to_date: filterData.toDate,
            export_type: exportType,
        });
        if (filterData.uhid?.trim()) params.append("patient_search", filterData.uhid.trim());
        if (Array.isArray(filterData.credit_type) && filterData.credit_type.length > 0) {
            params.append("credit_type", filterData.credit_type.join(","));
        }
        if (filterData.technique?.trim()) params.append("technique", filterData.technique.trim());
        if (filterData.therapy_name?.trim()) params.append("therapy_name", filterData.therapy_name.trim());
        return Serviceurls.RT_PATIENT_REPORT + "?" + params.toString();
    };

    const handleExportPdf = () => {
        try {
            RestAPIService.getAllPrint(buildExportParams("pdf"))
                .then((response) => {
                    const file = new Blob([response.data], { type: "application/pdf" });
                    const fileURL = URL.createObjectURL(file);
                    window.open(fileURL);
                })
                .catch((e) => {
                    errorMessage(e?.response?.data?.message || e?.message || "Export failed");
                });
        } catch (e) {
            errorMessage(e?.message || "Export failed");
        }
    };

    const handleExportExcel = () => {
        try {
            RestAPIService.excelGet(buildExportParams("excel"))
                .then((response) => {
                    if (response.data) {
                        const filename = response.headers["content-disposition"]?.split("filename=")[1]?.replace(/"/g, "") || "RtPatientReport.xlsx";
                        const blob = new Blob([response.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
                        const url = URL.createObjectURL(blob);
                        const link = document.createElement("a");
                        link.href = url;
                        link.setAttribute("download", filename);
                        link.click();
                        URL.revokeObjectURL(url);
                    }
                })
                .catch((e) => {
                    errorMessage(e?.response?.data?.message || e?.message || "Export failed");
                });
        } catch (e) {
            errorMessage(e?.message || "Export failed");
        }
    };

    const FilterDate = (from, to, index) => {
        setfilterData((prev) => ({ ...prev, fromDate: from, toDate: to, dateIndex: index }));
    };

    /** Default expanded (open); `false` in state means user collapsed that row. */
    const toggleRow = (index) => {
        setExpandedRows((prev) => {
            const isOpen = prev[index] !== false;
            return { ...prev, [index]: !isOpen };
        });
    };

    const handleChangePage = (_, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (e) => {
        setRowsPerPage(parseInt(e.target.value, 10));
        setPage(0);
    };

    const filteredBySearch = reportData.filter((row) => {
        if (!searchBarText?.trim()) return true;
        const pd = row.patient_details || {};
        const q = searchBarText.trim().toLowerCase();
        const matchUhid = (pd.uhid || "").toLowerCase().includes(q);
        const matchName = (pd.name || "").toLowerCase().includes(q);
        const matchMobile = (pd.mobile || "").toLowerCase().includes(q);
        return matchUhid || matchName || matchMobile;
    });

    const paginatedData = filteredBySearch.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    const closeFilter = () => {
        setFilterOpen(false);
    };

    const renderFilterScreen = () => {
        return (
            <Box className='eMed_rts_filter_container' >
                <Box className='eMed_filter_Top'>
                    <Typography variant='h6' fontWeight={600}>Filter</Typography>
                    <Tooltip>
                        <Button onClick={() => { setFilterOpen(false) }}>
                            <img src={ImagePaths.LabCloseButton.default} alt="close" className='eMed_Canteen_CloseImg' />
                        </Button>
                    </Tooltip>
                </Box>
                <Box className='eMed_rts_middle'>
                    <Box className='eMed_date_filter'>
                        <CommonDatePicker
                            FromDate={filterData.fromDate}
                            ToDate={filterData.toDate}
                            DateIndex={filterData.dateIndex}
                            SelectedDates={FilterDate.bind(this)}
                            HideAllMenu={true} />
                    </Box>
                    <Box className='eMed_rts_dropdown' sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                        <TextField
                            size="small"
                            fullWidth
                            label="Search by UHID"
                            value={filterData.uhid || ""}
                            placeholder="Enter UHID"
                            onChange={(e) => setfilterData((prev) => ({ ...prev, uhid: e.target.value }))}
                        />
                        <TextField
                            size="small"
                            fullWidth
                            label="Search by Technique"
                            value={filterData.technique || ""}
                            placeholder="Enter Technique"
                            onChange={(e) => setfilterData((prev) => ({ ...prev, technique: e.target.value }))}
                        />
                        <TextField
                            size="small"
                            fullWidth
                            label="Search by Therapy Name"
                            value={filterData.therapy_name || ""}
                            placeholder="Enter Therapy Name"
                            onChange={(e) => setfilterData((prev) => ({ ...prev, therapy_name: e.target.value }))}
                        />
                        <Box>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>Credit Type</Typography>
                            <Box sx={{ display: "flex", flexDirection: "row", gap: 1, flexWrap: "wrap" }}>
                                {CREDIT_TYPE_OPTIONS.map((opt) => (
                                    <FormControlLabel
                                        key={opt.value}
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={(filterData.credit_type || []).includes(opt.value)}
                                                onChange={(e) => {
                                                    const prev = filterData.credit_type || [];
                                                    const next = e.target.checked
                                                        ? [...prev, opt.value]
                                                        : prev.filter((v) => v !== opt.value);
                                                    setfilterData((p) => ({ ...p, credit_type: next }));
                                                }}
                                            />
                                        }
                                        label={opt.label}
                                    />
                                ))}
                            </Box>
                        </Box>
                    </Box>
                </Box>
                <Box>
                    <Stack spacing={2} direction="row" justifyContent={'center'}>
                        <Button size='small' variant='outlined' className='eMed_Canteen_BtnText' onClick={() => {
                            const cleared = {
                                ...defaultDate,
                                uhid: "",
                                credit_type: [],
                                technique: "",
                                therapy_name: "",
                            };
                            setfilterData(cleared);
                            setPage(0);
                            getReportData(cleared);
                        }}>Clear</Button>
                        <Button size='small' variant='contained' className='eMed_Canteen_BtnText' onClick={() => {
                            getReportData();
                            setFilterOpen(false);
                        }}>Search</Button>
                    </Stack>
                </Box>
            </Box>
        );
    };

    const patientDetailsAdapter = (patient_details) => {
        if (!patient_details) return {};
        const parts = (patient_details.age_gender || "").split("/").map((s) => s?.trim() || "").filter(Boolean);
        // Handles both "21Y/f" and "0Y/7M/18D/f": gender is last segment, age is the rest joined.
        const genderPart = parts.length > 0 ? parts[parts.length - 1] : "";
        const agePart = parts.length > 1 ? parts.slice(0, -1).join("/") : (parts[0] || "");
        return {
            patient_names: patient_details.name,
            name: patient_details.name,
            patient_uhid: patient_details.uhid,
            mobile_number: patient_details.mobile,
            patient_age: agePart || "-",
            patient_gender: genderPart || "-",
        };
    };

    return (
        <Box sx={{ height: "100%", width: "94vw", backgroundColor: Colors.Background, display: "flex", flexDirection: "column", minHeight: 0 }}>
            <Box display="flex" justifyContent="space-between" flexDirection="row" alignItems="center" flexShrink={0}>
                <Box display="flex" flexDirection="row" marginLeft="-0.8vw" mt="0.5vw">
                    {AmountsCard("Total Therapy Amount", summaryCards.total_payable, false)}
                    {AmountsCard("Total Previous Received", summaryCards.total_previous_received, false)}
                    {AmountsCard("Total Received", (summaryCards.total_received + Number(summaryCards.total_cancelled_received) || 0), false)}
                    {AmountsCard("Balance", summaryCards.total_balance, false)}
                </Box>
                <Box>
                    <Tooltip title="Filter">
                        <IconButton onClick={() => setFilterOpen(true)} aria-label="filter">
                            <img style={{ width: "2vw", height: "2vw" }} src={ImagePaths.Filter.default} alt="filter" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Export Excel">
                        <IconButton onClick={handleExportExcel} aria-label="export excel">
                            <img style={{ width: "2vw", height: "2vw" }} src={ImagePaths.ExportIcon.default} alt="export excel" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Export PDF">
                        <IconButton onClick={handleExportPdf} aria-label="export pdf">
                            <img style={{ width: "2vw", height: "2vw" }} src={ImagePaths.LabPrintIcon.default} alt="export pdf" />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            <Box
                flexShrink={0}
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "end",
                    marginTop: "0.5vw",
                    padding: "0.5vw 0.75vw",
                    backgroundColor: "white",
                    borderRadius: 1,
                    gap: 2,
                }}
            >
                <Typography variant="body2" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    From Date : <span style={{ fontSize: '0.9vw', fontWeight: 600, color: TABLE_HEADER_TEXT }}>{formatDate(filterData.fromDate)}</span>
                    To Date :<span style={{ fontSize: '0.9vw', fontWeight: 600, color: TABLE_HEADER_TEXT }}>{formatDate(filterData.toDate)}</span>
                </Typography>
                <TextField
                    size="small"
                    placeholder="Search..."
                    value={searchBarText || ""}
                    onChange={(e) => {
                        setSearchBarText(e.target.value);
                        setPage(0);
                    }}
                    sx={{ width: 250 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start" sx={{ height: "1.5vw" }}>
                                <SearchIcon sx={{ color: "action.active", fontSize: "1.2rem", height: "1.5vw" }} />
                            </InputAdornment>
                        ),
                    }}
                    inputProps={{ "aria-label": "Search by UHID, patient name, mobile number" }}
                />
            </Box>

            <Box sx={{ flex: 1, minHeight: 0, marginTop: "0.5vw", backgroundColor: "white", position: "relative", display: "flex", flexDirection: "column" }}>
                {isloader && (
                    <Box
                        sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "rgba(255,255,255,0.7)",
                            zIndex: 1,
                        }}
                    >
                        <CircularProgress />
                    </Box>
                )}
                <TableContainer component={Paper} sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
                    <Table stickyHeader size="small" aria-label="radiotherapy patient report">
                        <TableHead>
                            <TableRow>
                                {PATIENT_HEADERS.map((h) => (
                                    <TableCell key={h} sx={{ fontWeight: 600, backgroundColor: TABLE_HEADER_BG, color: TABLE_HEADER_TEXT }}>
                                        {h}
                                    </TableCell>
                                ))}
                                <TableCell padding="checkbox" sx={{ width: 40, backgroundColor: TABLE_HEADER_BG, color: TABLE_HEADER_TEXT }} />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedData.length === 0 && !isloader && (
                                <TableRow>
                                    <TableCell colSpan={PATIENT_HEADERS.length + 1} align="center" sx={{ py: 4 }}>
                                        No Records To Be Shown
                                    </TableCell>
                                </TableRow>
                            )}
                            {paginatedData.map((row, idx) => {
                                const globalIndex = page * rowsPerPage + idx;
                                const pd = row.patient_details || {};
                                const ps = row.patient_summary || {};
                                const events = row.events || [];
                                const open = events.length > 0 && expandedRows[globalIndex] !== false;
                                const patientRowData = patientDetailsAdapter(pd);
                                return (
                                    <React.Fragment key={globalIndex}>
                                        <TableRow hover>
                                            <TableCell>
                                                <Box component="div">
                                                    <CommonPatientDetails data={patientRowData} />
                                                </Box>
                                            </TableCell>
                                            <TableCell>{pd.tag ?? "N/A"}</TableCell>
                                            <TableCell>{AmountFormat(ps.therapy_amount)}</TableCell>
                                            <TableCell>{ps.total_fractions ?? "-"}</TableCell>
                                            <TableCell>{AmountFormat(ps.previous_payable_amount)}</TableCell>
                                            <TableCell>{AmountFormat(ps.received_amount + Number(ps.cancelled_received) || 0)}</TableCell>
                                            <TableCell>{AmountFormat(ps.balance_amount)}</TableCell>
                                            <TableCell padding="checkbox" sx={{ width: 40 }}>
                                                {events.length > 0 ? (
                                                    <IconButton size="small" onClick={() => toggleRow(globalIndex)} aria-label={open ? "collapse" : "expand"}>
                                                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                                    </IconButton>
                                                ) : null}
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell colSpan={PATIENT_HEADERS.length + 1} sx={{ py: 0, borderBottom: open ? undefined : "none" }}>
                                                <Collapse in={open} timeout="auto" unmountOnExit>
                                                    <Box sx={{ margin: 1, backgroundColor: "#fafafa", overflowX: "auto" }}>
                                                        <Table size="small">
                                                            <TableHead>
                                                                <TableRow>
                                                                    {EVENT_HEADERS.map((h) => (
                                                                        <TableCell key={h} sx={{ fontSize: "0.9vw", fontWeight: 600, whiteSpace: "nowrap" }}>
                                                                            {h}
                                                                        </TableCell>
                                                                    ))}
                                                                </TableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {events.map((ev, evIdx) => {
                                                                    const refundAmt = Number(ev.refund_amount) || 0;
                                                                    const refundFmt = AmountFormat(refundAmt);
                                                                    const previousReceivedAmt = Number(ev.previous_received) || 0;
                                                                    const previousReceivedFmt = AmountFormat(previousReceivedAmt);
                                                                    const receivedAmt = Number(ev.received_amount) || 0;
                                                                    const receivedFmt = AmountFormat(receivedAmt);
                                                                    const payDateStr = ev.date_of_payment ? formatDate(ev.date_of_payment) : "-";
                                                                    
                                                                    return (
                                                                        <Tooltip title={refundAmt > 0 ? 'Cancelled Therapy' : ''} arrow placement="top">
                                                                            <TableRow key={evIdx}>
                                                                                <TableCell sx={{ whiteSpace: "nowrap" }}>
                                                                                    <RtEventTextWithTooltip value={ev.therapy_details} />
                                                                                </TableCell>
                                                                                <TableCell sx={{ whiteSpace: "nowrap" }}>
                                                                                    <RtEventTextWithTooltip value={ev.site} />
                                                                                </TableCell>
                                                                                <TableCell sx={{ whiteSpace: "nowrap" }}>
                                                                                    <RtEventTextWithTooltip value={ev.dose_details} />
                                                                                </TableCell>
                                                                                <TableCell sx={{ whiteSpace: "nowrap" }}>
                                                                                    <RtEventTextWithTooltip value={ev.technique} />
                                                                                </TableCell>
                                                                                <TableCell sx={{ whiteSpace: "nowrap" }}>
                                                                                    <RtEventTextWithTooltip value={ev.doctor} />
                                                                                </TableCell>
                                                                                <TableCell sx={{ whiteSpace: "nowrap" }}>
                                                                                    <RtEventTextWithTooltip value={ev.stimulation} />
                                                                                </TableCell>
                                                                                <TableCell sx={{ whiteSpace: "nowrap" }}>
                                                                                    <RtEventTextWithTooltip value={ev.credit_type} />
                                                                                </TableCell>
                                                                                {/* <TableCell>{AmountFormat(ev.payable_amount)}</TableCell> */}
                                                                                <TableCell sx={{ whiteSpace: "nowrap" }}>
                                                                                    <RtEventValueWithTooltip title={previousReceivedFmt}>{previousReceivedFmt}</RtEventValueWithTooltip>
                                                                                </TableCell>
                                                                                <TableCell sx={{ whiteSpace: "nowrap", backgroundColor: refundAmt > 0 ? "red" : "transparent", color: refundAmt > 0 ? "white" : "inherit" }}>
                                                                                    <RtEventValueWithTooltip title={refundFmt}>{refundFmt}</RtEventValueWithTooltip>
                                                                                </TableCell>
                                                                                <TableCell sx={{ whiteSpace: "nowrap", backgroundColor: receivedAmt === 0 && !refundAmt > 0 ? "lightgray" : receivedAmt > 0 && refundAmt === 0 ? "green" : "transparent", color: receivedAmt === 0 && !refundAmt > 0 ? "black" : receivedAmt > 0 && refundAmt === 0 ? "white" : "inherit" }}>
                                                                                    <RtEventValueWithTooltip title={receivedFmt}>{receivedFmt}</RtEventValueWithTooltip>
                                                                                </TableCell>
                                                                                {/* <TableCell>{AmountFormat(ev.balance_amount)}</TableCell> */}
                                                                                <TableCell sx={{ whiteSpace: "nowrap" }}>
                                                                                    <RtEventValueWithTooltip title={payDateStr}>{payDateStr}</RtEventValueWithTooltip>
                                                                                </TableCell>
                                                                                <TableCell sx={{ whiteSpace: "nowrap" }}>
                                                                                    <RtEventTextWithTooltip value={ev.fractions} />
                                                                                </TableCell>
                                                                                <TableCell sx={{ whiteSpace: "nowrap" }}>
                                                                                    <RtEventTextWithTooltip value={ev.dvh} />
                                                                                </TableCell>
                                                                                <TableCell sx={{ whiteSpace: "nowrap" }}>
                                                                                    <RtEventTextWithTooltip value={ev.completed} />
                                                                                </TableCell>
                                                                                <TableCell sx={{ whiteSpace: "nowrap" }}>
                                                                                    <RtEventTextWithTooltip value={ev.pending} />
                                                                                </TableCell>
                                                                            </TableRow>
                                                                        </Tooltip>
                                                                    );
                                                                })}
                                                            </TableBody>
                                                        </Table>
                                                    </Box>
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>
                                    </React.Fragment>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={filteredBySearch.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[10, 20, 30]}
                    labelRowsPerPage="Rows per page:"
                />
            </Box>

            <Drawer
                anchor="right"
                open={filterOpen}
                ModalProps={{ onBackdropClick: closeFilter }}
            >
                {renderFilterScreen()}
            </Drawer>

            {errorMsg && (
                <ToastMsg severity="error" msg={errorMsgText} msgPop={() => { seterrorMsg(false); seterrorMsgText(""); }} />
            )}
            {successMsg && (
                <ToastMsg severity="success" msg={successMsgText} msgPop={() => { setsuccessMsg(false); setsuccessMsgText(""); }} />
            )}
        </Box>
    );
};

export default RtPatientReport;
